import React, { Component } from 'react';
import { withTheme } from '@emotion/react';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PopupMenu } from '../popup-menu';
import { ThemeContext } from '../theme';

const propTypes = {
  containerWidth: PropTypes.number,
  noDropdown: PropTypes.bool,
  children: PropTypes.array,
  dropDownId: PropTypes.string,
  containerStyles: PropTypes.object,
  showPlusIcon: PropTypes.bool,
  otherElementsWidth: PropTypes.number,
  minWidth: PropTypes.number,
  isDrawer: PropTypes.bool,
  shiftMethod: PropTypes.oneOf(['minWidth', 'availableWidth']),
  dropDownHeaderStyles: PropTypes.object,
  showCopyEditIcons: PropTypes.bool,
  dropDownTop: PropTypes.number,
  dropDownLeft: PropTypes.number,
  dropDownRight: PropTypes.number,
  enableScrolling: PropTypes.bool,
  onCopyClick: PropTypes.func,
  onEditClick: PropTypes.func
};

const defaultProps = {
  noDropdown: false,
  dropDownId: 'dropdown-display',
  showPlusIcon: false,
  containerStyles: {},
  minWidth: 500,
  isDrawer: false,
  shiftMethod: 'minWidth',
  dropDownHeaderStyles: {},
  showCopyEditIcons: false,
  enableScrolling: false
};

class TabsContainerComponent extends Component {
  state = {
    showDropdown: false,
    isDropdownOpen: false
  };

  container = React.createRef();
  dropDown = React.createRef();
  containerWidth = 0;

  // Theme context
  static contextType = ThemeContext;

  componentDidMount() {
    this.handleWindowResize();
    window.addEventListener('resize', debounce(this.handleWindowResize, 300));
  }

  componentDidUpdate(prevProps) {
    if (prevProps.containerWidth !== this.props.containerWidth ||
      prevProps.otherElementsWidth !== this.props.otherElementsWidth) {
      this.handleWindowResize();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  handleWindowResize = () => {
    if (!this.container || !this.container.current) {
      return;
    }

    let isDropdown = false;
    const { containerWidth, otherElementsWidth, minWidth, isDrawer, shiftMethod } = this.props;
    const availWidth = !isDrawer
      ? window.innerWidth - otherElementsWidth
      : containerWidth - otherElementsWidth;

    // console.log(`containerWidth = ${containerWidth} & minWidth = ${minWidth}`);
    // console.log(`window.innerWidth - otherElementsWidth = ${window.innerWidth - otherElementsWidth}`);
    // Show dropdown menu check
    switch (shiftMethod) {
      case 'availableWidth':
        // isDropdown = this.tabContainerWidth > availWidth;
        isDropdown = minWidth > availWidth;
        break;
      case 'minWidth':
      default:
        isDropdown = minWidth > availWidth;
        break;
    }

    // console.log(`isDropdown = ${isDropdown}`);

    this.setState({
      showDropdown: isDropdown
    });
  }

  toggleDropdown = () => {
    this.setState({
      isDropdownOpen: !this.state.isDropdownOpen
    });
  }

  handleOutsideClick = () => {
    this.setState({
      isDropdownOpen: false
    });
  }

  render() {
    if (this.dropDown.current) {
      window.dropdown = this.dropDown;
    }

    const dropdown = this.props.theme.dropdown ? this.props.theme.dropdown : this.context.theme.dropdown;
    const tabs = this.props.theme.tabs ? this.props.theme.tabs : this.context.theme.tabs;
    const {
      menuBackground,
      menuBorderColor,
      menuTextColor,
      fontSize,
      fontWeight,
      menuItemTextColor,
      menuItemActiveTextColor,
      menuItemHoverTextColor,
      menuItemDisabledTextColor,
      menuItemDisabledTextColorOpacity,
      menuItemBackgroundColor,
      menuItemActiveBackgroundColor,
      menuItemHoverBackgroundColor,
      menuItemDisabledBackgroundColor,
      menuItemIndicatorColor,
      menuItemActiveIndicatorColor,
      menuItemHoverIndicatorColor
    } = dropdown;
    const { tabsContainerBorder } = tabs;

    const styles = StyleSheet.create({
      iconContainer: {
        height: '40px',
        width: '40px',
        minWidth: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      icon: {
        fontSize: '22px'
      },
      dropdownMenu: {
        background: menuBackground,
        border: `1px solid ${menuBorderColor}`,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.5)',
        color: menuTextColor,
        fontSize: fontSize,
        fontWeight: fontWeight,
        // width: (this.dropDown && this.dropDown.current) ? this.dropDown.current.clientWidth : '250px'
        width: '200px'
      },
      selectedItemIcon: {
        height: '32px',
        width: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ':hover': {
          color: menuItemHoverTextColor,
          fill: menuItemHoverTextColor
        }
      },
      selectedItem: {
        display: 'flex',
        // width: '100%',
        height: '32px',
        // background: menuItemActiveBackgroundColor,
        color: menuItemActiveTextColor,
        fill: menuItemActiveTextColor,
        ':hover': {
          color: menuItemHoverTextColor,
          fill: menuItemHoverTextColor,
          background: menuItemHoverBackgroundColor
        }
      },
      menuItemIndicator: {
        width: '2px',
        height: '40px'
      },
      menuItemIcon: {
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ':hover:not(.activeitem)': {
          color: menuItemHoverTextColor,
          background: menuItemHoverBackgroundColor,
          fill: menuItemHoverTextColor
        }
      },
      menuItem: {
        padding: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'left',
        ':hover:not(.activeitem)': {
          color: menuItemHoverTextColor,
          background: menuItemHoverBackgroundColor
        }
      }
    });

    const items = [];
    let selectedItem = (
      <div
        style={{
          padding: '0 8px',
          paddingTop: '4px'
        }}
      >
        Dropdown
      </div>
    );
    // Dropdown items
    if (this.props.showDropdown) {
      this.props.children.forEach((el) => {
        if (el.props && el.props.isActive) {
          selectedItem = (
            <div
              ref={this.dropDown}
              onClick={() => this.toggleDropdown()}
              className={css(styles.selectedItem)}
            >
              {/* Dropdown selected item icon */}
              <div className={css(styles.selectedItemIcon)}>
                {el.props && el.props.showSvgIcon
                  ? el.props.svgIcon
                  : <i
                    className={`${el.props && el.props.iconClass}`}
                    style={{ fontSize: '22px' }}
                  />
                }
              </div>
              {/* Dropdown selected item text */}
              <div
                style={{
                  // padding: '6px 0 0 4px',
                  fontFamily: 'Lato',
                  fontWeight: 'bold',
                  // fontSize: '14px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {el.props.text}
              </div>
              {/* Dropdown arrow */}
              <i className='ml-2 fa fa-chevron-down' />
            </div>
          );
        }

        items.push(
          <div
            key={Math.random().toString(36).slice(2)}
            className={`${el.props && el.props.isActive ? 'activeitem' : ''} popup-menu-item noselect ${css(styles.menuItem)}`}
            onClick={() => {
              if (!el.props.isActive) {
                el.props.onActivate(el.props.tabKey);
              }
              this.setState({ isDropdownOpen: false });
            }}
          >
            {/* Dropdown item indicator */}
            <div
              className={css(styles.menuItemIndicator)}
              style={{
                background: (el.props && el.props.isActive) ? menuItemActiveIndicatorColor : menuItemIndicatorColor
              }}
            />
            {/* Dropdown item icon */}
            <div
              className={`${(el.props && el.props.isActive) ? 'activeitem' : ''} ${css(styles.menuItemIcon)}`}
              style={{
                color: el.props && el.props.isActive ? menuItemActiveTextColor : menuItemTextColor,
                fill: el.props && el.props.isActive ? menuItemActiveTextColor : menuItemTextColor
              }}
            >
              {el.props && el.props.showSvgIcon
                ? el.props.svgIcon
                : <i
                  className={el.props && el.props.iconClass ? el.props.iconClass : ''}
                  style={{ fontSize: '22px' }}
                />
              }
            </div>
            {/* Dropdown item text */}
            <div
              style={{
                color: el.props && el.props.isActive ? menuItemActiveTextColor : menuItemTextColor,
                width: '100%'
              }}
            >
              {el.prop && el.props.text ? el.props.text : ''}
            </div>

            {/* Dropdown item copy and edit icons */}
            {this.props.showCopyEditIcons
              ? <React.Fragment>
                <div
                  className="hover-blue"
                  onClick={this.props.onCopyClick}
                  style={{
                    width: '32px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fa fa-clone" />
                </div>
                <div
                  className="hover-blue"
                  onClick={this.props.onEditClick}
                  style={{
                    width: '32px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <i className="fa fa-pencil" />
                </div>
              </React.Fragment>
              : null
            }
          </div>
        );
      });
    }

    const tabItems = (
      <React.Fragment>
        <div
          ref={this.container}
          key="TAB_ITEMS_PARENT_DUMMY_KEY"
          className='tab-container'
          style={{
            ...this.props.containerStyles,
            display: this.state.showDropdown ? 'none' : 'flex',
            borderBottom: `1px solid ${tabsContainerBorder}`,
            overflowX: this.props.enableScrolling ? 'auto' : 'hidden',
            overflowY: 'hidden'
          }}
        >
          {/* Plus Icon */}
          {this.props.showPlusIcon
            ? <div className={css(styles.iconContainer)}>
              <i className={`fa fa-plus ${css(styles.icon)}`} />
            </div>
            : null
          }
          {this.props.children}
        </div>
      </React.Fragment>
    );

    // return tabs if noDropdown prop is set to true
    if (this.props.noDropdown) {
      return tabItems;
    }

    // Dropdown mode
    return (
      <React.Fragment>
        {/* Tabs display */}
        {/* {tabItems} */}
        <div
          ref={this.container}
          className='tab-container'
          style={{
            ...this.props.containerStyles,
            display: this.state.showDropdown ? 'none' : 'flex',
            borderBottom: `1px solid ${tabsContainerBorder}`,
            overflowX: this.props.enableScrolling ? 'auto' : 'hidden',
            overflowY: 'hidden'
          }}
        >
          {/* Plus Icon */}
          {this.props.showPlusIcon
            ? <div className={css(styles.iconContainer)}>
              <i className={`fa fa-plus ${css(styles.icon)}`} />
            </div>
            : null
          }
          {this.props.children}
        </div>

        {this.props.showDropdown &&
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}
          >
            {/* Plus Icon */}
            {this.props.showPlusIcon && this.state.showDropdown
              ? <div className={css(styles.iconContainer)}>
                <i className={`fa fa-plus ${css(styles.icon)}`} />
              </div>
              : null
            }
            {/* Dropdown display/selected item */}
            <div
              id={this.props.dropDownId}
              className="dropdown-display"
              style={{
                ...this.props.dropDownHeaderStyles,
                display: this.props.showDropdown ? 'flex' : 'none',
                marginLeft: '4px',
                // flexFlow: 'column',
                position: 'relative',
                alignItems: 'center'
              }}
            >
              {this.props.showDropdown && selectedItem}
              {/* Dropdown arrow */}
              <div style={{ display: 'none' }}>
              </div>
              {/* Dropdown display */}
              <PopupMenu
                visible={this.state.isDropdownOpen}
                parent={this.dropDown}
                onOutsideClick={this.handleOutsideClick}
                top={40}
                left={0}
                position='absolute'
              >
                <div className={css(styles.dropdownMenu)}>
                  {items}
                </div>
              </PopupMenu>
            </div>
          </div>
        }
      </React.Fragment>
    );
  }
}

TabsContainerComponent.propTypes = propTypes;
TabsContainerComponent.defaultProps = defaultProps;
export const TabsContainer = withTheme(TabsContainerComponent);
